/**
 * Runtime Factory and DI System
 *
 * Provides flexible runtime construction with:
 * - Provider factory registration
 * - Runtime presets (TUI, Test, Null)
 * - Lazy initialization
 * - Composition helpers
 */
import { NullUIProvider, NullSimulationProvider, NullEdiabasProvider, NullInp1Provider, NullPrintProvider, NullPemProvider, NullDtmProvider, NullExternalProvider, NullSpsProvider, } from './null/index.js';
// === Helper Functions ===
function resolveProvider(providerOrFactory, fallback) {
    if (providerOrFactory === undefined) {
        return fallback();
    }
    if (typeof providerOrFactory === 'function') {
        return providerOrFactory();
    }
    return providerOrFactory;
}
// === Runtime Builder ===
export class RuntimeBuilder {
    config = {};
    ui(provider) {
        this.config.ui = provider;
        return this;
    }
    simulation(provider) {
        this.config.simulation = provider;
        return this;
    }
    ediabas(provider) {
        this.config.ediabas = provider;
        return this;
    }
    inp1(provider) {
        this.config.inp1 = provider;
        return this;
    }
    print(provider) {
        this.config.print = provider;
        return this;
    }
    pem(provider) {
        this.config.pem = provider;
        return this;
    }
    dtm(provider) {
        this.config.dtm = provider;
        return this;
    }
    external(provider) {
        this.config.external = provider;
        return this;
    }
    sps(provider) {
        this.config.sps = provider;
        return this;
    }
    build() {
        return createRuntime(this.config);
    }
}
// === Main Factory ===
/**
 * Create a runtime with the given configuration.
 * Missing providers fall back to Null implementations.
 */
export function createRuntime(config = {}) {
    return {
        ui: resolveProvider(config.ui, () => new NullUIProvider()),
        simulation: resolveProvider(config.simulation, () => new NullSimulationProvider()),
        ediabas: resolveProvider(config.ediabas, () => new NullEdiabasProvider()),
        inp1: resolveProvider(config.inp1, () => new NullInp1Provider()),
        print: resolveProvider(config.print, () => new NullPrintProvider()),
        pem: resolveProvider(config.pem, () => new NullPemProvider()),
        dtm: resolveProvider(config.dtm, () => new NullDtmProvider()),
        external: resolveProvider(config.external, () => new NullExternalProvider()),
        sps: resolveProvider(config.sps, () => new NullSpsProvider()),
    };
}
/**
 * Start building a runtime with fluent API.
 */
export function buildRuntime() {
    return new RuntimeBuilder();
}
// === Presets ===
/**
 * Create a runtime with all Null providers.
 * Useful for testing or running scripts without side effects.
 */
export function createNullRuntime() {
    return createRuntime();
}
/**
 * Create a runtime for testing with optional overrides.
 */
export function createTestRuntime(overrides = {}) {
    return createRuntime(overrides);
}
// === Lazy Runtime ===
/**
 * Runtime that initializes providers lazily on first access.
 */
export class LazyRuntime {
    factories;
    _ui;
    _simulation;
    _ediabas;
    _inp1;
    _print;
    _pem;
    _dtm;
    _external;
    _sps;
    constructor(factories) {
        this.factories = factories;
    }
    get ui() {
        if (!this._ui) {
            this._ui = resolveProvider(this.factories.ui, () => new NullUIProvider());
        }
        return this._ui;
    }
    get simulation() {
        if (!this._simulation) {
            this._simulation = resolveProvider(this.factories.simulation, () => new NullSimulationProvider());
        }
        return this._simulation;
    }
    get ediabas() {
        if (!this._ediabas) {
            this._ediabas = resolveProvider(this.factories.ediabas, () => new NullEdiabasProvider());
        }
        return this._ediabas;
    }
    get inp1() {
        if (!this._inp1) {
            this._inp1 = resolveProvider(this.factories.inp1, () => new NullInp1Provider());
        }
        return this._inp1;
    }
    get print() {
        if (!this._print) {
            this._print = resolveProvider(this.factories.print, () => new NullPrintProvider());
        }
        return this._print;
    }
    get pem() {
        if (!this._pem) {
            this._pem = resolveProvider(this.factories.pem, () => new NullPemProvider());
        }
        return this._pem;
    }
    get dtm() {
        if (!this._dtm) {
            this._dtm = resolveProvider(this.factories.dtm, () => new NullDtmProvider());
        }
        return this._dtm;
    }
    get external() {
        if (!this._external) {
            this._external = resolveProvider(this.factories.external, () => new NullExternalProvider());
        }
        return this._external;
    }
    get sps() {
        if (!this._sps) {
            this._sps = resolveProvider(this.factories.sps, () => new NullSpsProvider());
        }
        return this._sps;
    }
}
/**
 * Create a lazy runtime that initializes providers on first access.
 */
export function createLazyRuntime(factories) {
    return new LazyRuntime(factories);
}
//# sourceMappingURL=factory.js.map